import React, { Fragment, useState } from "react"
import { StyledDatePickerPopup } from "components/Common/StyledPopup"
import {
  HomeBanner,
  NavigateButton,
  NavigationLeft,
  NavigationRow,
  StyledDay,
  SwitchCaloriesViewPopover,
  SwitchViewButton,
  ToggleCaloriesViewMenu,
  ToggleCaloriesViewText,
} from "./styled"
import DatePicker from "components/pickers/DatePicker"
import { FlexBox } from "components/Common/FlexBox"
import { useDailyProgress, useMeals, useUpdateSelectedDate } from "../../hooks"
import dayjs from "dayjs"
import { DATE_PARAM_FORMAT, US_DATE_FORMAT_WITH_YEAR } from "config"
import DropdownDownIcon from "components/Icons/DropdownDownIcon"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Action } from "antd-mobile/es/components/action-sheet"
import useUserPreferences from "features/profile/hooks/useUserPreferences"
import { IDailyCaloriesMode } from "features/profile/profileTypes"
import NavigateLeftCircleIcon from "components/Icons/NavigateLeftCircleIcon"
import NavigateRightCircleIcon from "components/Icons/NavigateRightCircleIcon"
import PATHS from "router/paths"
import { useLogBrazeEvent } from "libs/braze"

const ChartIcon = () => (
  <svg
    style={{ flexShrink: 0 }}
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      d="M12 13.3332V6.6665M8 13.3332V2.6665M4 13.3332V9.33317"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ToggleCaloriesViewIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M7.67714 12.6508C7.67714 12.1968 7.51206 11.7841 7.18191 11.4746C6.85176 11.1444 6.4597 11 5.98511 11C5.53115 11 5.1391 11.1651 4.80895 11.4746C4.4788 11.8047 4.31372 12.1968 4.31372 12.6508C4.31372 12.9603 4.39626 13.2285 4.5407 13.4968C4.68514 13.7444 4.89149 13.9507 5.1391 14.0952C5.38671 14.2396 5.6756 14.3221 5.96448 14.3221C6.27399 14.3221 6.56288 14.2396 6.83112 14.0952C7.07874 13.9507 7.28508 13.7444 7.45016 13.4968C7.5946 13.2285 7.67714 12.9603 7.67714 12.6508ZM13.6611 12.6508C13.6611 12.1968 13.4961 11.7841 13.1865 11.4746C12.8564 11.1444 12.4643 11 12.0104 11C11.5564 11 11.1644 11.1651 10.8342 11.4746C10.5041 11.8047 10.3596 12.1968 10.3596 12.6508C10.3596 12.9603 10.4422 13.2285 10.5866 13.4968C10.731 13.7444 10.9374 13.9507 11.185 14.0952C11.4326 14.2396 11.7215 14.3221 12.0104 14.3221C12.3199 14.3221 12.6088 14.2396 12.8564 14.0952C13.104 13.9507 13.3103 13.7444 13.4548 13.4968C13.5786 13.2285 13.6611 12.9603 13.6611 12.6508ZM19.6864 12.6508C19.6864 12.1968 19.5213 11.7841 19.1912 11.4746C18.861 11.1444 18.469 11 18.015 11C17.5404 11 17.1484 11.1651 16.8182 11.4746C16.4881 11.8047 16.323 12.1968 16.323 12.6508C16.323 12.9603 16.4055 13.2285 16.55 13.4968C16.6944 13.7444 16.9007 13.9507 17.169 14.0952C17.4166 14.2396 17.7055 14.3221 18.0356 14.3221C18.3452 14.3221 18.6134 14.2396 18.861 14.0952C19.1086 13.9507 19.315 13.7444 19.4594 13.4968C19.6039 13.2285 19.6864 12.9603 19.6864 12.6508Z"
      fill="white"
    />
  </svg>
)

const DiaryNavigation = ({ refreshTime }: { refreshTime: number }) => {
  const { date, onDateChange } = useMeals(refreshTime)

  const isToday = date.isSame(dayjs().startOf("day"))

  const [showCalendar, setShowCalendar] = useState(false)

  useUpdateSelectedDate()
  useDailyProgress(refreshTime)

  const [_, setSearchParams] = useSearchParams()
  const onBackToYesterday = () => {
    setSearchParams({ date: date.subtract(1, "day").format(DATE_PARAM_FORMAT) })
  }

  const onGoToTomorrow = () => {
    setSearchParams({ date: date.add(1, "day").format(DATE_PARAM_FORMAT) })
  }

  const { userPreferences, onChangeDailyCaloriesMode } = useUserPreferences()

  const dailyCaloriesMode = userPreferences.dailyCaloriesMode

  const navigate = useNavigate()
  const goToOverview = () => {
    navigate(PATHS.app.overview)
  }

  const { logBrazeDailyTrendView } = useLogBrazeEvent()

  const actions: Action[] = [
    {
      key: "over-view",
      text: (
        <ToggleCaloriesViewMenu
          onClick={() => {
            goToOverview()
            logBrazeDailyTrendView()
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="17"
            viewBox="0 0 16 17"
            fill="none"
          >
            <path
              d="M2 2.5C2.36802 2.50022 2.66602 2.79892 2.66602 3.16699V12.167C2.66602 12.7192 3.11386 13.1668 3.66602 13.167H14C14.3682 13.167 14.667 13.4658 14.667 13.834C14.667 14.2022 14.3682 14.501 14 14.501H2.66602C1.92972 14.5008 1.33301 13.9033 1.33301 13.167V3.16699C1.33301 2.79878 1.63179 2.5 2 2.5ZM11.7598 6.17383C11.8984 5.87758 12.2505 5.74926 12.5469 5.8877C12.8433 6.02615 12.9713 6.37932 12.833 6.67578L12.832 6.67676L12.8311 6.67871L12.8281 6.68555L12.8164 6.70898C12.8068 6.72878 12.7931 6.75702 12.7754 6.79199C12.7401 6.86185 12.6895 6.96044 12.624 7.07812C12.4937 7.31244 12.3039 7.62951 12.0674 7.9541C11.8327 8.27614 11.5394 8.62231 11.2002 8.90332C10.865 9.18093 10.4427 9.4296 9.95312 9.4834C9.42161 9.54174 9.00193 9.35041 8.67969 9.08301C8.39287 8.84496 8.16255 8.52739 7.9834 8.28027C7.97711 8.2716 7.97101 8.2624 7.96484 8.25391C7.76156 7.97374 7.61244 7.77472 7.45215 7.6416C7.31707 7.52949 7.20683 7.49218 7.06445 7.50781C6.89313 7.52664 6.68346 7.62337 6.44336 7.82227C6.20741 8.0178 5.9811 8.27972 5.7793 8.55664C5.57924 8.83117 5.41531 9.10459 5.30078 9.31055C5.24384 9.41293 5.19912 9.49691 5.16992 9.55469C5.15534 9.58354 5.14464 9.6058 5.1377 9.62012L5.13086 9.63574L5.12891 9.63867C4.9904 9.93493 4.63818 10.063 4.3418 9.9248C4.04534 9.78634 3.91733 9.43321 4.05566 9.13672V9.13574L4.06055 9.12598L4.07129 9.10254C4.08086 9.08281 4.09478 9.05519 4.1123 9.02051C4.14763 8.95063 4.19918 8.8521 4.26465 8.73438C4.39495 8.50007 4.58476 8.18298 4.82129 7.8584C5.05593 7.53643 5.34836 7.19013 5.6875 6.90918C6.02267 6.63153 6.44499 6.38298 6.93457 6.3291C7.46627 6.27067 7.88667 6.46198 8.20898 6.72949C8.49583 6.96762 8.72612 7.28512 8.90527 7.53223C8.91156 7.5409 8.91767 7.5501 8.92383 7.55859C9.12712 7.83878 9.27618 8.03782 9.43652 8.1709C9.57161 8.283 9.68182 8.32034 9.82422 8.30469C9.99547 8.28576 10.2044 8.18898 10.4443 7.99023C10.6804 7.79465 10.9075 7.53295 11.1094 7.25586C11.3094 6.98134 11.4734 6.7079 11.5879 6.50195C11.6447 6.39977 11.6886 6.31556 11.7178 6.25781C11.7323 6.22913 11.7431 6.2067 11.75 6.19238L11.7578 6.17676L11.7598 6.17383Z"
              fill="white"
            />
          </svg>
          <ToggleCaloriesViewText>Overview</ToggleCaloriesViewText>
        </ToggleCaloriesViewMenu>
      ),
    },
    {
      key: "toggle-progress-view",
      text: (
        <ToggleCaloriesViewMenu onClick={onChangeDailyCaloriesMode}>
          <ChartIcon />
          <ToggleCaloriesViewText>
            View Calorie{" "}
            {dailyCaloriesMode === IDailyCaloriesMode.INTAKE
              ? "Remaining"
              : "Intake"}
          </ToggleCaloriesViewText>
        </ToggleCaloriesViewMenu>
      ),
    },
  ]

  const [
    isSwitchCaloriesViewPopoverVisible,
    setSwitchCaloriesViewPopoverVisible,
  ] = useState(false)

  return (
    <Fragment>
      <StyledDatePickerPopup
        position="top"
        visible={showCalendar}
        onMaskClick={() => setShowCalendar(false)}
      >
        <DatePicker
          onChange={(date) => {
            onDateChange(date)
            setShowCalendar(false)
          }}
          value={date}
        />
      </StyledDatePickerPopup>

      <HomeBanner>
        <NavigationRow>
          <NavigationLeft $width={isToday ? 165 : 228}>
            <NavigateButton onClick={onBackToYesterday}>
              <NavigateLeftCircleIcon />
            </NavigateButton>
            <FlexBox
              $alignItems="center"
              $gap={4}
              onClick={() => setShowCalendar(true)}
            >
              <StyledDay>
                {isToday ? "Today" : date.format(US_DATE_FORMAT_WITH_YEAR)}
              </StyledDay>
              <DropdownDownIcon fill="#fff" />
            </FlexBox>

            <NavigateButton onClick={onGoToTomorrow}>
              <NavigateRightCircleIcon />
            </NavigateButton>
          </NavigationLeft>

          <SwitchCaloriesViewPopover
            $popoverInnerLeft={
              dailyCaloriesMode === IDailyCaloriesMode.INTAKE ? -166 : -138
            }
            style={
              {
                "--content-padding": "0px",
              } as React.CSSProperties
            }
            actions={actions}
            placement="left-end"
            trigger="click"
            onAction={() => {}}
            onVisibleChange={(visible) => {
              setSwitchCaloriesViewPopoverVisible(visible)
            }}
          >
            <SwitchViewButton $isActive={isSwitchCaloriesViewPopoverVisible}>
              <ToggleCaloriesViewIcon />
            </SwitchViewButton>
          </SwitchCaloriesViewPopover>
        </NavigationRow>
      </HomeBanner>
    </Fragment>
  )
}

export default DiaryNavigation
